import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  LinearProgress,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  CheckCircle,
  Error,
  Person,
  Email,
  Phone,
  Badge,
  CalendarToday,
  Payment,
  Security,
  Assessment,
  Schedule,
  CloudUpload,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  FindInPage as FindInPageIcon,
  MailOutline as MailOutlineIcon,
  SettingsOutlined,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../store/languageStore';
import authService from '../shared/services/authService';
import AppHeader from '../shared/components/AppHeader';

const ProfilePage: React.FC = () => {
  const theme = useTheme();
  const { user, updateUser } = useAuthStore();
  const { t, tString } = useTranslation();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editData, setEditData] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    phonenumber: user?.phonenumber || '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Activity Statistics State
  const [activityStats, setActivityStats] = useState<{
    total_documents: number;
    document_processing: number;
    tariff_classification: number;
    email_processing: number;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccessMessage(null);
    setErrors({});
    setEditData({
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      phonenumber: user?.phonenumber || '',
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setSuccessMessage(null);
    setErrors({});
    setEditData({
      firstname: user?.firstname || '',
      lastname: user?.lastname || '',
      phonenumber: user?.phonenumber || '',
    });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!editData.firstname.trim()) {
      newErrors.firstname = tString('auth.firstNameRequired');
    }

    if (!editData.lastname.trim()) {
      newErrors.lastname = tString('auth.lastNameRequired');
    }

    // Phone number validation (optional but must be valid if provided)
    if (editData.phonenumber && !/^[+]?[\d\s\-()]{10,}$/.test(editData.phonenumber.trim())) {
      newErrors.phonenumber = tString('auth.invalidPhoneNumber');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);

      // Clean up phone number (remove if empty)
      const profileData = {
        firstname: editData.firstname.trim(),
        lastname: editData.lastname.trim(),
        phonenumber: editData.phonenumber.trim() || undefined,
      };

      const updatedUser = await authService.updateProfile(profileData);

      // Update the user in the store
      updateUser(updatedUser);

      // Show success message
      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setError(error.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load activity statistics
  useEffect(() => {
    const loadActivityStats = async () => {
      try {
        setStatsLoading(true);
        const stats = await authService.getActivityStats();
        setActivityStats(stats);
      } catch (error) {
        console.error('Failed to load activity stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    if (user) {
      loadActivityStats();
    }
  }, [user]);

  const usagePct = user?.monthly_document_limit && user.monthly_document_limit !== -1
    ? ((user?.monthly_documents_used || 0) / user.monthly_document_limit) * 100
    : 0;

  return (
    <>
      <AppHeader />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* ── Profile hero ─────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              {/* Error and Success Messages */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 80, height: 80, bgcolor: theme.palette.primary.main, fontSize: '2rem' }}>
                    {user?.firstname?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {user?.full_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      @{user?.username}
                    </Typography>
                    <Chip
                      label={user?.is_admin ? 'Administrator' : 'User'}
                      color={user?.is_admin ? 'warning' : 'primary'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Box>

                {!isEditing ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={handleEdit}
                  >
                    {t('common.edit')}
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
                      onClick={handleSave}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : t('common.save')}
                    </Button>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {isEditing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="profile-firstname"
                      data-field-name="firstname"
                      label={t('auth.firstName')}
                      value={editData.firstname}
                      onChange={(e) => {
                        setEditData({ ...editData, firstname: e.target.value });
                        if (errors.firstname) setErrors({ ...errors, firstname: '' });
                      }}
                      fullWidth
                      variant="outlined"
                      required
                      error={!!errors.firstname}
                      helperText={errors.firstname}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="profile-lastname"
                      data-field-name="lastname"
                      label={t('auth.lastName')}
                      value={editData.lastname}
                      onChange={(e) => {
                        setEditData({ ...editData, lastname: e.target.value });
                        if (errors.lastname) setErrors({ ...errors, lastname: '' });
                      }}
                      fullWidth
                      variant="outlined"
                      required
                      error={!!errors.lastname}
                      helperText={errors.lastname}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      id="profile-phonenumber"
                      data-field-name="phonenumber"
                      label={t('auth.phoneNumber')}
                      value={editData.phonenumber}
                      onChange={(e) => {
                        setEditData({ ...editData, phonenumber: e.target.value });
                        if (errors.phonenumber) setErrors({ ...errors, phonenumber: '' });
                      }}
                      fullWidth
                      variant="outlined"
                      placeholder="Optional"
                      error={!!errors.phonenumber}
                      helperText={errors.phonenumber || 'Optional field'}
                      disabled={isLoading}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <ProfileDetail icon={<Email fontSize="small" />} label={tString('auth.email')} value={user?.email} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ProfileDetail icon={<Person fontSize="small" />} label={tString('profile.fullName')} value={`${user?.firstname} ${user?.lastname}`} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ProfileDetail icon={<Phone fontSize="small" />} label={tString('auth.phoneNumber')} value={user?.phonenumber || tString('profile.notSet')} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ProfileDetail icon={<Badge fontSize="small" />} label="Username" value={user?.username} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <ProfileDetail
                      icon={<CalendarToday fontSize="small" />}
                      label="Member Since"
                      value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    />
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* ── Settings shortcut ────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              onClick={() => navigate('/settings')}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                transition: 'background-color 0.2s, border-color 0.2s',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <SettingsOutlined color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t('profile.settingsHintTitle')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('profile.settingsHintSubtitle')}
                </Typography>
              </Box>
              <ArrowForward fontSize="small" color="action" />
            </Paper>
          </Grid>

          {/* ── Account status ───────────────────────────────────────── */}
          <Grid item xs={12} md={5}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('profile.accountStatus')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('profile.accountStatus')}
                    secondary={
                      <Chip
                        label={user?.is_active ? t('profile.active') : t('profile.inactive')}
                        color={user?.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('profile.lastUpdated')}
                    secondary={user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* ── Activity statistics ──────────────────────────────────── */}
          <Grid item xs={12} md={7}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Assessment color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Activity Statistics
                </Typography>
                {statsLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Box>
              {statsLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : activityStats ? (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {activityStats.total_documents}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Documents
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <DescriptionIcon sx={{ fontSize: 32, color: 'success.main', mb: 0.5 }} />
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {activityStats.document_processing}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Document Processing
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <FindInPageIcon sx={{ fontSize: 32, color: 'warning.main', mb: 0.5 }} />
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                          {activityStats.tariff_classification}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tariff Classification
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <MailOutlineIcon sx={{ fontSize: 32, color: 'info.main', mb: 0.5 }} />
                        <Typography variant="h4" fontWeight={700} color="info.main">
                          {activityStats.email_processing}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Email Processing
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="info">No activity data available.</Alert>
              )}
            </Paper>
          </Grid>

          {/* Plan Details, Features, and Billing - Hidden for Admins */}
          {!user?.is_admin && (
            <>
              {/* ── Plan details & usage ───────────────────────────────── */}
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    background: user?.selected_plan === 'enterprise'
                      ? 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)'
                      : user?.selected_plan === 'advanced'
                      ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                      : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
                    color: user?.selected_plan === 'enterprise' ? '#2e7d32' : user?.selected_plan === 'advanced' ? '#f57c00' : '#6B6B6B',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h5" fontWeight={700}>
                      {user?.selected_plan === 'enterprise' ? 'Enterprise Plan' :
                       user?.selected_plan === 'advanced' ? 'Advanced Plan' :
                       'Essential Plan'}
                    </Typography>
                    <Chip
                      label={user?.plan_status === 'active' ? 'Active' :
                            user?.plan_status === 'expired' ? 'Expired' :
                            user?.plan_status === 'suspended' ? 'Suspended' : 'Pending'}
                      color={user?.plan_status === 'active' ? 'success' : 'warning'}
                      size="medium"
                      sx={{ backgroundColor: 'rgba(0,0,0,0.1)', color: 'inherit', fontWeight: 600 }}
                    />
                  </Box>

                  {/* Usage Statistics Grid */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Credits Used / Total */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            <CloudUpload sx={{ mr: 1 }} />
                            <Typography variant="h4" fontWeight={700}>
                              {user?.monthly_documents_used || 0}
                              {user?.monthly_document_limit !== -1 && (
                                <Typography component="span" variant="h6" fontWeight={400} sx={{ opacity: 0.7 }}>
                                  &nbsp;/&nbsp;{user?.monthly_document_limit?.toLocaleString() || 'N/A'}
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            {t('profile.documentsUsedThisMonth')}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {user?.monthly_document_limit === -1
                              ? t('profile.unlimitedDocuments')
                              : `${usagePct.toFixed(0)}% of credits used`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Usage Percentage */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                            <CircularProgress
                              variant="determinate"
                              value={user?.monthly_document_limit === -1 ? 0 : Math.min(usagePct, 100)}
                              size={60}
                              thickness={6}
                              sx={{ color: 'currentColor' }}
                            />
                            <Box
                              sx={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: 'absolute',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography variant="body2" fontWeight={700}>
                                {user?.monthly_document_limit === -1 ? '∞' : Math.round(usagePct) + '%'}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            {t('profile.usagePercentage')}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              user?.monthly_document_limit === -1 ? t('profile.unlimitedDocuments') :
                              usagePct > 80 ? t('profile.usageNearLimit') :
                              usagePct > 50 ? 'Moderate' : 'Low Usage'
                            }
                            color={
                              user?.monthly_document_limit === -1 ? 'success' :
                              usagePct > 80 ? 'warning' :
                              usagePct > 50 ? 'info' : 'success'
                            }
                            variant="outlined"
                            sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Days Until Reset */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            <Schedule sx={{ mr: 1 }} />
                            <Typography variant="h4" fontWeight={700}>
                              {user?.monthly_reset_date ?
                                Math.max(0, Math.ceil((new Date(user!.monthly_reset_date!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) :
                                'N/A'}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            {t('profile.daysUntilReset')}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {user?.monthly_reset_date ? new Date(user!.monthly_reset_date!).toLocaleDateString() : 'No reset date'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Data Retention */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ backgroundColor: 'rgba(255,255,255,0.7)', height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            <Assessment sx={{ mr: 1 }} />
                            <Typography variant="h4" fontWeight={700}>
                              {user?.data_retention_days || 30}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            {t('profile.dataRetentionPeriod')}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Your data is stored securely
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Usage Progress Bar (for non-unlimited plans) */}
                  {user?.monthly_document_limit !== -1 && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Credits Used
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {(user?.monthly_documents_used || 0).toLocaleString()} / {user?.monthly_document_limit?.toLocaleString() || 'N/A'} credits
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(usagePct, 100)}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: 'currentColor',
                            borderRadius: 6,
                          },
                        }}
                      />
                      {usagePct > 80 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <WarningIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {t('profile.usageNearLimit')}. Consider upgrading your plan.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Plan Summary */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {t('profile.planAdopted')}: {user?.plan_adopted_date ? new Date(user!.plan_adopted_date!).toLocaleDateString() : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {t('profile.nextBilling')}: {user?.next_billing_date ? new Date(user!.next_billing_date!).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: 'currentColor',
                        color: 'inherit',
                        '&:hover': {
                          borderColor: 'currentColor',
                          backgroundColor: 'rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      {t('profile.managePlan')}
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* ── Plan features ──────────────────────────────────────── */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('profile.planFeatures')}
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        {user?.has_advanced_extraction ? <CheckCircle color="success" /> : <Error color="disabled" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.advancedExtraction')}
                        secondary={user?.has_advanced_extraction ? t('profile.enabled') : t('profile.notAvailable')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {user?.has_document_tampering_detection ? <CheckCircle color="success" /> : <Error color="disabled" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.tamperingDetection')}
                        secondary={user?.has_document_tampering_detection ? t('profile.enabled') : t('profile.notAvailable')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {user?.has_workflow_automation ? <CheckCircle color="success" /> : <Error color="disabled" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.workflowAutomation')}
                        secondary={user?.has_workflow_automation ? t('profile.enabled') : t('profile.notAvailable')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {user?.has_priority_support ? <CheckCircle color="success" /> : <Error color="disabled" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.prioritySupport')}
                        secondary={user?.has_priority_support ? t('profile.support24x7') : t('profile.standardSupport')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        {user?.has_custom_api_development ? <CheckCircle color="success" /> : <Error color="disabled" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.customApiDevelopment')}
                        secondary={user?.has_custom_api_development ? t('profile.available') : t('profile.notAvailable')}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              {/* ── Billing information ────────────────────────────────── */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t('profile.billingInformation')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <CalendarToday color="primary" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            Plan Adopted
                          </Typography>
                          <Typography variant="h6" fontWeight={500}>
                            {user?.plan_adopted_date ? new Date(user!.plan_adopted_date!).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <CalendarToday color="warning" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            Next Billing Date
                          </Typography>
                          <Typography variant="h6" fontWeight={500}>
                            {user?.next_billing_date ? new Date(user!.next_billing_date!).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Payment color="success" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            Payment Status
                          </Typography>
                          <Chip
                            label={user?.payment_status === 'paid' ? 'Paid' :
                                  user?.payment_status === 'pending' ? 'Pending' :
                                  user?.payment_status === 'overdue' ? 'Overdue' : 'Failed'}
                            color={user?.payment_status === 'paid' ? 'success' :
                                  user?.payment_status === 'pending' ? 'warning' : 'error'}
                            size="small"
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Payment color="info" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="textSecondary">
                            Payment Method
                          </Typography>
                          <Typography variant="h6" fontWeight={500} sx={{ fontSize: '0.9rem' }}>
                            {user?.payment_method ?
                              user!.payment_method!.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) :
                              'Not set'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {user?.payment_reference && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Payment Reference
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {user?.payment_reference}
                      </Typography>
                    </Box>
                  )}

                  {user?.billing_address && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Billing Address
                      </Typography>
                      <Typography variant="body1">
                        {user?.billing_address}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </>
  );
};

/** Compact labelled detail used in the profile hero (view mode). */
const ProfileDetail: React.FC<{ icon: React.ReactNode; label: string; value?: string | null }> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} noWrap>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

export default ProfilePage;
